import React from 'react';
import { useI18n } from '../../context/I18nContext';
import CableTessera from './CableTessera';
import { Icons } from '../icons/Icons';
import { getCableDisplayName, getMultipolarCablesDisplayNames, getTerminalGroupsForCables } from '../../utils/cableLogic';

const NestedCableList = ({
  nodes = [],
  settings,
  onCableClick,
  onEditCable,
  onEditMultipolarCable,
  onDeleteCable,
  onDeleteMultipolarCable,
  hoveredCable,
  setHoveredCable,
  handleDragStart,
  handleDragEnd,
  handleDragOver,
  handleDragEnter,
  handleDragLeave,
  handleDrop,
  draggedIndex,
  dragOverIndex,
  editingIndex,
  editingMultipolarIndex,
  showDeleteButtons,
  onToggleDelete,
  isSelectionModeEnabled,
  selectedCables,
  onToggleSelection,
  availableSheaths,
  allCables = null,
  getCurrentGroupTitle,
  handleGroupTitleChange,
  handleGroupTitleBlur,
  handleGroupTitleKeyDown,
  validateCable
}) => {
  const { t } = useI18n();

  // La capienza di un morsetto va verificata su tutto il cablaggio, non sui soli cavi
  // del box: due cavi che arrivano allo stesso punto restano in conflitto anche se
  // finiscono in gruppi diversi.
  const validationScopeFor = (contextCables) => (allCables?.length ? allCables : contextCables);

  // I gruppi di morsetto condiviso appartengono all'insieme di cavi, non al singolo
  // cavo: si calcolano una volta per contesto e si riusano per tutte le tessere che
  // lo condividono (le sorelle di un box ricevono lo stesso array).
  const terminalGroupsCache = new Map();
  const terminalGroupsFor = (contextCables) => {
    if (!contextCables) return null;
    if (!terminalGroupsCache.has(contextCables)) {
      terminalGroupsCache.set(contextCables, getTerminalGroupsForCables(contextCables, settings));
    }
    return terminalGroupsCache.get(contextCables);
  };

  /*
    Modificato `renderSingle` per accettare un override opzionale per nascondere la siglatura e il badge guaina.
  */
  const renderSingle = (node, hideSiglaturaOverride = false, hideSheathBadgeOverride = false, relatedCablesOverride = null) => {
    // Logica di visualizzazione siglatura:
    // 1. Se override è true (ereditato da genitore multiplo), NASCONDI.
    // 2. Altrimenti, usa logica locale: nascondi se è un nodo multiplo ridondante.
    const isRedundantMultiple = node.type === 'multiple' && !settings.marcaturePerCollegamento;
    const showSiglatura = !hideSiglaturaOverride && !isRedundantMultiple;
    const validationContext = relatedCablesOverride || node.cables;
    const terminalGroups = terminalGroupsFor(validationContext);

    return node.cables.map(cable => {
      const isDragging = draggedIndex === cable.originalIndex;
      const isDragOver = dragOverIndex === cable.originalIndex && draggedIndex !== cable.originalIndex;
      const isHovered = hoveredCable === cable.originalIndex;
      const validation = validateCable(cable, validationScopeFor(validationContext), settings.lingua, settings.useAWG, settings);
      const hasIssues = !validation.isValid;
      const displaySiglatura = getCableDisplayName(cable, settings.lingua, settings);

      return (
        <div
          key={`nested-single-${cable.originalIndex}`}
          className="relative pr-2"
          draggable={settings.isDragModeEnabled}
          onDragStart={(e) => handleDragStart?.(e, cable.originalIndex)}
          onDragEnd={handleDragEnd}
          onDragOver={(e) => handleDragOver?.(e, cable.originalIndex)}
          onDragEnter={(e) => handleDragEnter?.(e, cable.originalIndex)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop?.(e, cable.originalIndex)}
          onMouseEnter={() => !settings.isDragModeEnabled && setHoveredCable?.(cable.originalIndex)}
          onMouseLeave={() => !settings.isDragModeEnabled && setHoveredCable?.(null)}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {settings.isDragModeEnabled && (
            <div
              className={`absolute left-4 text-gray-400 hover:text-blue-600 transition-colors z-10 ${cable.poli && Array.isArray(cable.poli)
                ? 'top-7'
                : 'top-1/2 transform -translate-y-1/2'
                }`}
            >
              <Icons.DragHandle className="w-4 h-4" />
            </div>
          )}

          <CableTessera
            cable={cable}
            validateCable={(targetCable) => validateCable(targetCable, validationScopeFor(validationContext), settings.lingua, settings.useAWG, settings)}
            displaySiglatura={displaySiglatura}
            onCableClick={onCableClick}
            onEditCable={onEditCable}
            onEditMultipolarCable={onEditMultipolarCable}
            onDeleteCable={onDeleteCable}
            onDeleteMultipolarCable={onDeleteMultipolarCable}
            showSiglatura={showSiglatura}
            clickable={!settings.isDragModeEnabled && !isSelectionModeEnabled}
            settings={settings}
            isDragEnabled={settings.isDragModeEnabled}
            isDragging={isDragging}
            isDragOver={isDragOver}
            isHovered={isHovered}
            hasIssues={hasIssues}
            editingIndex={editingIndex}
            editingMultipolarIndex={editingMultipolarIndex}
            showDeleteButton={showDeleteButtons.has(cable.originalIndex)}
            onToggleDelete={() => onToggleDelete(cable.originalIndex)}
            isSelectionModeEnabled={isSelectionModeEnabled}
            selectedCables={selectedCables}
            onToggleSelection={onToggleSelection}
            handleDragStart={handleDragStart}
            handleDragEnd={handleDragEnd}
            handleDragOver={handleDragOver}
            handleDragEnter={handleDragEnter}
            handleDragLeave={handleDragLeave}
            handleDrop={handleDrop}
            draggedIndex={draggedIndex}
            dragOverIndex={dragOverIndex}
            availableSheaths={availableSheaths}
            relatedCables={validationContext}
            terminalGroups={terminalGroups}
            hideSheathBadge={hideSheathBadgeOverride}
          />
        </div>
      );
    });
  };

  // Helper per estrarre il nome del gruppo dalla chiave
  const extractGroupName = (node) => {
    // Estrai il nome del gruppo da groupCode, defaultTitle, o groupKey
    let groupName = node.groupCode || node.defaultTitle;

    // Se groupName non è disponibile, prova a estrarlo da groupKey o id
    if (!groupName) {
      const key = node.groupKey || node.id;
      // Se la chiave contiene "forced_group_", estrai il nome del gruppo
      if (key && key.startsWith('forced_group_')) {
        // Rimuovi "forced_group_" e il contatore finale (es: "_0")
        const match = key.match(/^forced_group_(.+?)(?:_\d+)?$/);
        if (match) {
          groupName = match[1];
        } else {
          groupName = key;
        }
      } else {
        groupName = key;
      }
    }

    return groupName;
  };

  const getNodeTitle = (node) => {
    if (node.type === 'group') {
      const groupName = extractGroupName(node);

      // Usa getCurrentGroupTitle per ottenere il titolo personalizzato
      const title = getCurrentGroupTitle?.(groupName);
      if (title) return title;

      // Fallback al nome del gruppo
      return groupName || node.label || t('cableList.group');
    }

    if (node.type === 'sheath' && node.sheathId) {
      const sheath = availableSheaths?.find(s => s.id === node.sheathId);
      const baseLabel = sheath
        ? (sheath.fullLabel || `${t('cableList.sheathLabel')} ${sheath.label}`)
        : `${t('cableList.sheathLabel')} ${node.sheathId}`;

      // Una guaina spezzata in più box va numerata, altrimenti i box sono indistinguibili.
      return node.subgroupIndex && node.totalSubgroups > 1
        ? `${baseLabel} (${node.subgroupIndex}/${node.totalSubgroups})`
        : baseLabel;
    }

    const firstCable = node.cables?.[0];
    if (!firstCable) return node.label || t('cableList.cable');

    if (settings?.marcaturePerCollegamento) {
      const cableNumbers = node.cables
        ?.map(cable => `#${(cable.originalIndex ?? 0) + 1}`)
        .join(', ');
      const numbersLabel = (node.cables?.length || 0) > 1 ? t('cableList.cablesLabel') : t('cableList.cableLabel');
      return `${numbersLabel} ${cableNumbers}`;
    }

    // Un gruppo può contenere più cavi multipolari: elencali tutti nel titolo.
    const multipolarNames = getMultipolarCablesDisplayNames(node.cables, settings.lingua);
    const displaySiglatura = multipolarNames.length > 0
      ? multipolarNames.join(', ')
      : getCableDisplayName(firstCable, settings.lingua, settings);
    const cableLabel = multipolarNames.length > 1 ? t('cableList.cablesLabel') : t('cableList.cableLabel');
    return `${cableLabel} ${displaySiglatura}`;
  };

  /*
    renderNode aggiornato per supportare la propagazione della visibilità della siglatura e del badge guaina.
    inheritHideSiglatura: se true, i figli non mostreranno la siglatura.
    inheritHideSheathBadge: se true, i figli non mostreranno il badge guaina.
  */
  const renderNode = (node, nestingLevel = 0, inheritHideSiglatura = false, inheritHideSheathBadge = false, relatedCablesContext = null) => {
    if (node.type === 'single') {
      return (
        <React.Fragment key={node.id}>
          {renderSingle(node, inheritHideSiglatura, inheritHideSheathBadge, relatedCablesContext)}
        </React.Fragment>
      );
    }

    const isSheath = node.type === 'sheath';
    const isGroup = node.type === 'group';
    const isMultiple = node.type === 'multiple';
    const title = getNodeTitle(node);

    // Un gruppo multiplo può contenere più cavi multipolari (es. due multipolari che
    // condividono un morsetto). I cavi standard del gruppo non vanno mai resi come
    // tessere sorelle: sono già disegnati dentro il polo del multipolare a cui si
    // collegano (renderConnectedCable), altrimenti comparirebbero due volte.
    const multipolarCables = isMultiple
      ? (node.cables || [])
        .filter(cable => cable.poli && Array.isArray(cable.poli))
        .sort((a, b) => (a.originalIndex ?? 0) - (b.originalIndex ?? 0))
      : [];

    const renderMultipolarTessera = (multipolarCable, hideSheathBadge) => renderSingle(
      {
        ...node,
        id: `multipolar-composite-${multipolarCable.originalIndex}`,
        type: 'single',
        cables: [multipolarCable]
      },
      false,
      hideSheathBadge,
      node.cables
    );

    // Un solo multipolare: la sua tessera rappresenta già tutto il gruppo, il box
    // non aggiungerebbe informazione.
    if (multipolarCables.length === 1) {
      return (
        <React.Fragment key={node.id}>
          {renderMultipolarTessera(multipolarCables[0], inheritHideSheathBadge)}
        </React.Fragment>
      );
    }

    // Determina se i figli devono nascondere la siglatura.
    // Se questo nodo è Multiplo e le impostazioni lo richiedono, allora nascondi nei figli.
    // Oppure se è già nascosto dal padre.
    const shouldHideChildrenSiglatura = inheritHideSiglatura || (isMultiple && !settings.marcaturePerCollegamento);

    // Determina se i figli devono nascondere il badge guaina.
    // 1. Se il nodo è MULTIPLO, resetta il nascondere (il badge va mostrato).
    // 2. Altrimenti, se è guaina o eredita nascondere, nascondi.
    const shouldHideChildrenSheathBadge = isMultiple ? false : (inheritHideSheathBadge || isSheath);

    const badge = isSheath
      ? t('cableList.sheath')
      : isGroup
        ? (node.groupCode || node.defaultTitle || t('cableList.group'))
        : t('cableList.multiple');
    const totalCables = node.cables?.length || 0;
    const subGroupCount = node.subGroups?.length || 0;
    const hasIssues = node.cables?.some(cable => !validateCable(cable, validationScopeFor(node.cables), settings.lingua, settings.useAWG, settings).isValid);
    const allSelected = node.cables?.every(cable => selectedCables.has(cable.originalIndex));
    const currentLevel = node.level ?? 1;

    const handleHeaderClick = () => {
      if (!settings.isDragModeEnabled && !isSelectionModeEnabled && node.cables?.[0]) {
        const first = node.cables[0];
        onCableClick?.(first.siglatura, first.originalIndex);
      }
    };

    const handleToggleNodeSelection = (e) => {
      e.stopPropagation();
      if (!isSelectionModeEnabled) return;
      node.cables.forEach(cable => {
        const isSelected = selectedCables.has(cable.originalIndex);
        if (allSelected && isSelected) {
          onToggleSelection(cable.originalIndex);
        } else if (!allSelected && !isSelected) {
          onToggleSelection(cable.originalIndex);
        }
      });
    };

    const childContent = multipolarCables.length > 1
      ? multipolarCables.map(multipolarCable => (
        <React.Fragment key={`multipolar-child-${multipolarCable.originalIndex}`}>
          {renderMultipolarTessera(multipolarCable, shouldHideChildrenSheathBadge)}
        </React.Fragment>
      ))
      : node.subGroups?.length > 0
        ? node.subGroups.map(child => renderNode(
          child,
          nestingLevel + 1,
          shouldHideChildrenSiglatura,
          shouldHideChildrenSheathBadge,
          isMultiple ? node.cables : (relatedCablesContext || node.cables)
        ))
        : renderSingle(node, shouldHideChildrenSiglatura, shouldHideChildrenSheathBadge, relatedCablesContext || node.cables);

    // Nei nodi "multiplo" i sottogruppi sono solo le foglie per-cavo, non veri
    // sottogruppi: il contatore deve riportare i cavi e basta.
    const cablesText = totalCables === 1 ? `1 ${t('cableList.cable')}` : `${totalCables} ${t('cableList.cables')}`;
    const infoText = subGroupCount > 0 && !isMultiple
      ? `${cablesText} ${t('cableList.inSubgroups', { count: subGroupCount, word: subGroupCount === 1 ? t('cableList.subgroup') : t('cableList.subgroups') })}`
      : cablesText;

    const indentWrapperClass = currentLevel > 1 ? '' : '';
    const containerPaddingClass = currentLevel > 1 ? 'py-3 pl-3 pr-0' : 'p-3 pr-0';

    return (
      <div
        key={node.id}
        className={`border rounded-lg ${containerPaddingClass} transition-colors cursor-pointer w-full ${hasIssues
          ? 'bg-amber-50 border-amber-200 hover:bg-amber-100 border-l-4 border-l-amber-400'
          : isSheath
            ? 'bg-sky-50 border-sky-200 hover:bg-sky-100 border-l-4 border-l-sky-400'
            : isGroup
              ? 'bg-purple-50 border-purple-200 hover:bg-purple-100 border-l-4 border-l-purple-400'
              : 'bg-green-50 border-green-200 hover:bg-green-100 border-l-4 border-l-green-400'
          }`}
        onClick={handleHeaderClick}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex-1 mr-4">
            <div className="flex items-center gap-3">
              {isSelectionModeEnabled && (
                <label className="flex items-center cursor-pointer flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={handleToggleNodeSelection}
                    className="w-5 h-5 text-green-600 border-2 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
                  />
                </label>
              )}

              {isGroup ? (
                <input
                  type="text"
                  value={getCurrentGroupTitle?.(extractGroupName(node)) ?? title}
                  onChange={(e) => handleGroupTitleChange?.(extractGroupName(node), e.target.value)}
                  onBlur={() => handleGroupTitleBlur?.(extractGroupName(node))}
                  onKeyDown={(e) => handleGroupTitleKeyDown?.(e, extractGroupName(node))}
                  className="w-full px-3 py-2 text-base font-semibold border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                  placeholder={t('cableList.group')}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <span className="font-medium text-base">{title}</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0 pr-2">
            <div className="text-sm text-gray-600 text-right">
              {infoText}
            </div>

            <div className={`px-4 py-1 rounded-full text-sm font-medium ml-2 ${isSheath
              ? 'bg-sky-100 text-sky-800'
              : isGroup
                ? 'bg-purple-100 text-purple-800'
                : 'bg-green-100 text-green-800'
              }`}>
              {badge}
            </div>

            {hasIssues && (
              <span className="text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded-full">
                ⚠️ {t('cableList.hasProblems')}
              </span>
            )}
          </div>
        </div>

        <div className={`${indentWrapperClass}`}>
          {/* La linea verticale di gruppo è ora integrata nel bordo sinistro del box del gruppo */}
          <div
            className="space-y-2"
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDragEnter={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {childContent}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-2 min-w-0 w-full">
      {nodes.map(node => renderNode(node, 0))}
    </div>
  );
};

export default NestedCableList;
